import React from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import KpiCard from './KpiCard';
import { formatNumber } from '../../reports/reportHelper';
import { getScenarioReportConfig } from '../scenarioConfig';

const ScenarioReport = ({ result }) => {
  const { t } = useTranslation();
  if (!result) return null;

  const reportConfig = getScenarioReportConfig(t)[result.model];
  if (!reportConfig) return null;

  const { header, kpis: kpiConfig, charts: chartConfig } = reportConfig;
  const { kpis = {}, charts = {} } = result;

  const renderKpi = () => {
    if (!kpiConfig?.length) return null;

    const primaryKpis = kpiConfig.filter(k => k.primary);
    const secondaryKpis = kpiConfig.filter(k => !k.primary);

    return (
      <>
        <Row className="mb-3">
          {primaryKpis.map((kpi) => {
            const value = kpis[kpi.key];
            if (value === undefined || value === null) return null;

            return (
              <Col md={4} key={kpi.key}>
                <KpiCard
                  title={kpi.title}
                  value={`${kpi.prefix || ''}${formatNumber(value)}${kpi.suffix || ''}`}
                  unit={kpi.unit}
                  variant={kpi.variant}
                  level="primary"
                />
              </Col>
            );
          })}
        </Row>

        {/* Secondary KPIs */}
        {secondaryKpis.length > 0 && (
          <Row>
            {secondaryKpis.map((kpi) => {
              const value = kpis[kpi.key];
              if (value === undefined || value === null) return null;

              return (
                <Col md={4} key={kpi.key}>
                  <KpiCard
                    title={kpi.title}
                    value={`${kpi.prefix || ''}${formatNumber(value)}${kpi.suffix || ''}`}
                    unit={kpi.unit}
                    level="secondary"
                  />
                </Col>
              );
            })}
          </Row>
        )}
      </>
    );
  };

  const getChartOptions = (isCost) => {
    const chartOptions = {
      legend: { display: false },
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        callbacks: {
          label: function (tooltipItem) {
            const value = tooltipItem.yLabel;
            return isCost ? `£${formatNumber(value)}` : `${formatNumber(value)} tCO₂e`;
          },
        },
      },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: function (value) {
                return isCost ? `£${formatNumber(value)}` : `${formatNumber(value)} tCO₂e`;
              },
            },
            gridLines: { color: '#E5E7EB' },
          },
        ],
        xAxes: [{ gridLines: { display: false } }],
      },
    };
    return chartOptions;
  }


  const renderChart = () => {
    if (!chartConfig || !charts) return null;

    return (
      <Row className="mt-4">
        {chartConfig.emissions && charts.emissions && (
          <Col md={6}>
            <Card>
              <Card.Body>
                <div className="font-weight-bold mb-1">
                  {chartConfig.emissions.title}
                </div>
                <div className="text-muted small mb-3">
                  {chartConfig.emissions.desc}
                </div>

                <div style={{ height: 260 }}>
                  <Bar
                    options={getChartOptions(false)}
                    data={{
                      labels: chartConfig.emissions.labels,
                      datasets: [
                        {
                          backgroundColor: ['#EF4444', '#22C55E'],
                          data: [
                            charts.emissions.current,
                            charts.emissions.withUpgrade,
                          ],
                        },
                      ],
                    }}
                  />
                </div>

                {kpis.co2Reduction !== undefined && (
                  <div className="text-center mt-3">
                    <Badge variant="success">
                      {t('{{co2}} tCO₂e Reduction', { co2e: formatNumber(kpis.co2Reduction) })}
                    </Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {/* Cost chart */}
        {chartConfig.cost && charts.cost && (
          <Col md={6}>
            <Card>
              <Card.Body>
                <div className="font-weight-bold mb-1">
                  {chartConfig.cost.title}
                </div>
                <div className="text-muted small mb-3">
                  {chartConfig.cost.desc}
                </div>

                <div style={{ height: 260 }}>
                  <Bar
                    data={{
                      labels: chartConfig.cost.labels,
                      datasets: [
                        {
                          backgroundColor: ['#EF4444', '#22C55E'],
                          data: [
                            charts.cost.before,
                            charts.cost.after,
                          ],
                        },
                      ],
                    }}
                    options={getChartOptions(true)}
                  />
                </div>

                {kpis.annualSavings !== undefined && (
                  <div className="text-center mt-3">
                    <Badge variant="primary">
                      {t('£{{value}} savings per year', { value: formatNumber(kpis.annualSavings) })}
                    </Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    );
  };

  return (
    <Card className="mt-4">
      <Card.Body>
        <div className="font-weight-bold mb-1">
          {header.title}
        </div>
        <div className="text-muted small mb-4">
          {header.subtitle}
        </div>

        {renderKpi()}
        {renderChart()}
      </Card.Body>
    </Card>
  );
};

export default ScenarioReport;
